import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, X, Rocket } from "lucide-react";

/**
 * Org onboarding kit (item 2): post-signup starter checklist.
 * Auto-checks off steps from real data signals; "share" completes on copy.
 * Dismissible; hidden once all four steps are done or it was dismissed.
 */
export function OrgStarterChecklist({ user, myOpps }) {
  const uid = user.user_id;
  const dismissKey = `org_checklist_dismissed_${uid}`;
  const sharedKey = `org_checklist_shared_${uid}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === "1");
  const [shared, setShared] = useState(() => localStorage.getItem(sharedKey) === "1");
  const [approvedCount, setApprovedCount] = useState(null);

  useEffect(() => {
    api.hoursByStatus("approved")
      .then((rows) => setApprovedCount(rows.length))
      .catch(() => setApprovedCount(0));
  }, []);

  const impactUrl = `${window.location.origin}/o/${uid}/impact`;
  const shareBlurb = `We're on CivicHearth — volunteer with us here: ${impactUrl}`;
  const markShared = () => {
    localStorage.setItem(sharedKey, "1");
    setShared(true);
  };
  const copyText = async (text, successMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      markShared();
      toast.success(successMsg);
    } catch {
      // Clipboard API can be unavailable (permissions/older browsers): fall
      // back to execCommand, and surface the text either way.
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        markShared();
        toast.success(successMsg);
      } catch {
        markShared();
        toast.message(text);
      }
    }
  };
  const copyImpact = () => copyText(impactUrl, "Impact page link copied — share it anywhere");
  const copyBlurb = () => copyText(shareBlurb, "Blurb copied — paste it into your newsletter or socials");

  const steps = [
    {
      id: "profile",
      label: "Complete your profile",
      done: !!user.onboarded,
      action: <Link to="/onboarding" className="text-forest underline">Finish profile</Link>,
    },
    {
      id: "opportunity",
      label: "Post your first opportunity",
      done: myOpps.length > 0,
      action: <Link to="/post-opportunity" className="text-forest underline">Post one now</Link>,
    },
    {
      id: "hours",
      label: "Approve your first volunteer hours",
      done: (approvedCount ?? 0) > 0,
      action: <span className="text-neutral-500">Approvals appear below once volunteers log hours</span>,
    },
    {
      id: "share",
      label: "Share your public impact page",
      done: shared,
      action: (
        <span className="inline-flex items-center gap-2 flex-wrap">
          <button data-testid="checklist-share-copy" onClick={copyImpact} className="text-forest underline">
            Copy link
          </button>
          <span className="text-neutral-300">·</span>
          <button
            data-testid="checklist-share-blurb"
            onClick={copyBlurb}
            className="text-forest underline"
            title={shareBlurb}
          >
            Copy ready-made blurb
          </button>
        </span>
      ),
    },
  ];

  const allDone = steps.every(s => s.done);
  if (dismissed || allDone || approvedCount === null) return null;

  const doneCount = steps.filter(s => s.done).length;

  return (
    <div data-testid="org-starter-checklist" className="mt-8 bg-white border border-terracotta/25 rounded-2xl p-6 relative">
      <button
        data-testid="checklist-dismiss"
        aria-label="Dismiss checklist"
        onClick={() => { localStorage.setItem(dismissKey, "1"); setDismissed(true); }}
        className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2">
        <Rocket className="w-5 h-5 text-terracotta" />
        <div className="font-display text-xl text-forest">Getting started</div>
        <span data-testid="checklist-progress" className="text-xs text-neutral-500 ml-1">{doneCount} of {steps.length} done</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {steps.map((s) => (
          <div key={s.id} data-testid={`checklist-step-${s.id}`} data-done={s.done} className="flex items-start gap-3 text-sm">
            {s.done
              ? <CheckCircle2 className="w-4.5 h-4.5 w-[18px] h-[18px] text-forest shrink-0 mt-0.5" />
              : <Circle className="w-[18px] h-[18px] text-neutral-300 shrink-0 mt-0.5" />}
            <span className={s.done ? "text-neutral-400 line-through" : "text-neutral-800"}>{s.label}</span>
            {!s.done && <span className="text-xs mt-0.5">{s.action}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
